import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, Trash2, Save, X, Layers, Users, Package, Edit3, Copy } from "lucide-react";
import { PREP_CATEGORIES } from "../lib/data";
import { useContent } from "../lib/content";

const CATS = [
  ...PREP_CATEGORIES,
  { id: "custom", label: "Custom Category" },
];

const DEFAULT_DATE_FIELDS = [
  { key: "strategy_meeting", label: "Strategy Meeting" },
  { key: "staging", label: "Staging" },
  { key: "cleaners", label: "Cleaners" },
  { key: "photo_day", label: "Photo / Video Day" },
  { key: "listing_launch", label: "Listing Launch" },
  { key: "open_house", label: "Open House" },
  { key: "closing", label: "Closing" },
];

const BLANK_BUNDLE = {
  name: "",
  description: "",
  custom_tasks: [],
  agent_note: "",
  key_dates: {},
  custom_vendors: [],
};

export default function AdminBundles() {
  const [bundles, setBundles] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // bundle being edited (or BLANK_BUNDLE for new)
  const [applyingId, setApplyingId] = useState(null); // bundle id being applied
  const [snapshotFor, setSnapshotFor] = useState(false); // open snapshot-from-seller modal
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);

  const load = async () => {
    try {
      const [b, s] = await Promise.all([
        api.get("/admin/bundles"),
        api.get("/admin/sellers"),
      ]);
      setBundles(b.data.bundles || []);
      setSellers(s.data.sellers || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const flash = (msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 6000);
  };
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this template? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/bundles/${id}`);
      setBundles((xs) => xs.filter((b) => b.id !== id));
      flash("Template deleted.");
    } catch (e) {
      flash(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const onSaved = (saved, isNew) => {
    setBundles((xs) => isNew ? [saved, ...xs] : xs.map((b) => b.id === saved.id ? saved : b));
    setEditing(null);
    flash(isNew ? "Template saved." : "Template updated.");
  };

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="overline">Admin · Templates</span>
          <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
            Reusable client setups
          </h1>
          <p style={{ color: "var(--text-muted)", maxWidth: 640 }}>
            Save reusable templates of custom tasks, agent notes, key dates, and vendors — then apply them to one or many clients in a single click.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button data-testid="bundle-snapshot-open" onClick={() => setSnapshotFor(true)} className="btn-ghost" style={{ padding: "9px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Copy size={14} /> Save from existing seller
          </button>
          <button data-testid="bundle-create-open" onClick={() => setEditing({ ...BLANK_BUNDLE })} className="btn-primary" style={{ padding: "10px 16px", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Plus size={14} /> New template
          </button>
        </div>
      </div>

      {toast && <div data-testid="bundle-toast" style={{ marginTop: 16, padding: "10px 14px", background: "var(--surface-alt)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 14 }}>{toast}</div>}

      {loading ? (
        <div style={{ marginTop: 24, color: "var(--text-muted)" }}>Loading…</div>
      ) : bundles.length === 0 ? (
        <div className="card" style={{ marginTop: 24, padding: 32, textAlign: "center", color: "var(--text-muted)" }} data-testid="bundles-empty">
          <Package size={28} style={{ color: "var(--primary)", marginBottom: 10 }} />
          <div style={{ fontWeight: 500, color: "var(--text)" }}>No templates yet.</div>
          <div style={{ fontSize: 14, marginTop: 4 }}>Create one to reuse a set of tasks/notes/dates across sellers.</div>
        </div>
      ) : (
        <div style={{ marginTop: 24, display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }} data-testid="bundles-grid">
          {bundles.map((b) => (
            <div key={b.id} className="card" style={{ padding: 22, display: "flex", flexDirection: "column", gap: 10 }} data-testid={`bundle-${b.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Layers size={16} style={{ color: "var(--primary)" }} />
                <span className="font-serif" style={{ fontSize: 18, fontWeight: 500 }}>{b.name}</span>
              </div>
              {b.description && <div style={{ color: "var(--text-muted)", fontSize: 13 }}>{b.description}</div>}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                <span className="badge">{(b.custom_tasks || []).length} task{(b.custom_tasks || []).length === 1 ? "" : "s"}</span>
                {b.agent_note && <span className="badge">note</span>}
                {Object.values(b.key_dates || {}).some(Boolean) && <span className="badge">{Object.values(b.key_dates || {}).filter(Boolean).length} date{Object.values(b.key_dates || {}).filter(Boolean).length === 1 ? "" : "s"}</span>}
                <span className="badge">{(b.custom_vendors || []).length} vendor{(b.custom_vendors || []).length === 1 ? "" : "s"}</span>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: "auto", paddingTop: 12 }}>
                <button data-testid={`bundle-apply-${b.id}`} onClick={() => setApplyingId(b.id)} className="btn-primary" style={{ flex: 1, padding: "8px 12px", fontSize: 13, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <Users size={14} /> Apply
                </button>
                <button data-testid={`bundle-edit-${b.id}`} onClick={() => setEditing(b)} className="btn-ghost" style={{ padding: "8px 12px", fontSize: 13 }}>
                  <Edit3 size={14} />
                </button>
                <button data-testid={`bundle-delete-${b.id}`} onClick={() => remove(b.id)} className="btn-ghost" style={{ padding: "8px 12px", fontSize: 13, color: "var(--secondary)" }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <BundleEditor
          bundle={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
      {applyingId && (
        <ApplyBundleModal
          bundle={bundles.find((b) => b.id === applyingId)}
          sellers={sellers}
          onClose={() => setApplyingId(null)}
          onApplied={(result) => {
            setApplyingId(null);
            const n = result.applied?.length || 0;
            flash(`Applied to ${n} client${n === 1 ? "" : "s"}.`);
          }}
        />
      )}
      {snapshotFor && (
        <SnapshotFromSellerModal
          sellers={sellers}
          onClose={() => setSnapshotFor(false)}
          onCreated={(saved) => {
            setSnapshotFor(false);
            setBundles((xs) => [saved, ...xs]);
            flash("Template saved from this client.");
          }}
        />
      )}
    </div>
  );
}

/* ---------- Editor ---------- */
function BundleEditor({ bundle, onClose, onSaved }) {
  const isNew = !bundle.id;
  const { content } = useContent();
  const dateFields = (content?.date_fields && content.date_fields.length > 0) ? content.date_fields : DEFAULT_DATE_FIELDS;
  const [name, setName] = useState(bundle.name || "");
  const [description, setDescription] = useState(bundle.description || "");
  const [tasks, setTasks] = useState(bundle.custom_tasks || []);
  const [note, setNote] = useState(bundle.agent_note || "");
  const [dates, setDates] = useState(bundle.key_dates || {});
  const [vendors, setVendors] = useState(bundle.custom_vendors || []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [nt, setNt] = useState({ category: "repairs", custom_category_label: "", title: "", note: "", due_date: "" });
  const [nv, setNv] = useState({ name: "", category: "", phone: "", email: "", url: "", note: "" });

  const addTask = () => {
    if (!nt.title.trim()) return;
    setTasks((xs) => [...xs, { ...nt, id: `tmp_${Math.random().toString(36).slice(2, 10)}` }]);
    setNt({ category: nt.category, custom_category_label: "", title: "", note: "", due_date: "" });
  };
  const removeTask = (i) => setTasks((xs) => xs.filter((_, idx) => idx !== i));
  const addVendor = () => {
    if (!nv.name.trim()) return;
    setVendors((xs) => [...xs, { ...nv, id: `tmpv_${Math.random().toString(36).slice(2, 10)}` }]);
    setNv({ name: "", category: "", phone: "", email: "", url: "", note: "" });
  };
  const removeVendor = (i) => setVendors((xs) => xs.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) { setErr("Give this template a name."); return; }
    setBusy(true); setErr("");
    const payload = {
      name: name.trim(),
      description: description.trim(),
      custom_tasks: tasks.map(({ id, ...rest }) => (id && id.startsWith("tmp_")) ? rest : { id, ...rest }),
      agent_note: note,
      key_dates: dates,
      custom_vendors: vendors.map(({ id, ...rest }) => (id && id.startsWith("tmpv_")) ? rest : { id, ...rest }),
    };
    try {
      const { data } = isNew
        ? await api.post("/admin/bundles", payload)
        : await api.put(`/admin/bundles/${bundle.id}`, payload);
      onSaved(data.bundle, isNew);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Couldn't save template.");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title={isNew ? "New template" : "Edit template"} testid="bundle-editor">
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={lblStyle}>Template name</label>
          <input data-testid="bundle-name" className="input-field" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Luxury staging launch" />
        </div>
        <div>
          <label style={lblStyle}>Short description (optional)</label>
          <input data-testid="bundle-desc" className="input-field" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="When you'd reach for this template" />
        </div>

        <Divider label="Custom tasks" />
        <div className="form-grid">
          <div>
            <label style={lblStyle}>Category</label>
            <select className="input-field" value={nt.category} onChange={(e) => setNt({ ...nt, category: e.target.value })}>
              {CATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          {nt.category === "custom" && (
            <div>
              <label style={lblStyle}>Custom category name</label>
              <input className="input-field" value={nt.custom_category_label} onChange={(e) => setNt({ ...nt, custom_category_label: e.target.value })} placeholder="e.g. Before Staging" />
            </div>
          )}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lblStyle}>Task</label>
            <input data-testid="bundle-task-title" className="input-field" value={nt.title} onChange={(e) => setNt({ ...nt, title: e.target.value })} placeholder="e.g. Confirm professional cleaner" />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lblStyle}>Note (optional)</label>
            <input className="input-field" value={nt.note} onChange={(e) => setNt({ ...nt, note: e.target.value })} placeholder="Vendor or reminder" />
          </div>
          <div>
            <label style={lblStyle}>Due date (optional)</label>
            <input type="date" className="input-field" value={nt.due_date} onChange={(e) => setNt({ ...nt, due_date: e.target.value })} />
          </div>
        </div>
        <button data-testid="bundle-task-add" type="button" className="btn-secondary" onClick={addTask} style={{ alignSelf: "flex-start" }}>
          <Plus size={14} /> Add task
        </button>
        {tasks.length > 0 && (
          <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
            {tasks.map((t, i) => (
              <div key={t.id || i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderTop: i ? "1px solid var(--border)" : "none", gap: 10 }} data-testid={`bundle-task-row-${i}`}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>{t.title}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {(t.category === "custom" ? (t.custom_category_label || "Custom") : (CATS.find((c) => c.id === t.category)?.label || t.category))}
                    {t.note ? ` · ${t.note}` : ""}{t.due_date ? ` · due ${t.due_date}` : ""}
                  </div>
                </div>
                <button className="btn-ghost" onClick={() => removeTask(i)} data-testid={`bundle-task-remove-${i}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}

        <Divider label="Agent note" />
        <textarea data-testid="bundle-note" className="input-field" rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional message that'll be appended to each seller's agent note when applied." />

        <Divider label="Key dates" />
        <div className="form-grid">
          {dateFields.map((f) => (
            <div key={f.key}>
              <label style={lblStyle}>{f.label}</label>
              <input type="date" className="input-field" value={dates[f.key] || ""} onChange={(e) => setDates({ ...dates, [f.key]: e.target.value })} />
            </div>
          ))}
        </div>

        <Divider label="Vendors" />
        <div className="form-grid">
          <div>
            <label style={lblStyle}>Name</label>
            <input className="input-field" value={nv.name} onChange={(e) => setNv({ ...nv, name: e.target.value })} placeholder="Vendor name" />
          </div>
          <div>
            <label style={lblStyle}>Category</label>
            <input className="input-field" value={nv.category} onChange={(e) => setNv({ ...nv, category: e.target.value })} placeholder="Stager, painter, etc." />
          </div>
          <div>
            <label style={lblStyle}>Phone</label>
            <input className="input-field" value={nv.phone} onChange={(e) => setNv({ ...nv, phone: e.target.value })} />
          </div>
          <div>
            <label style={lblStyle}>Email</label>
            <input className="input-field" value={nv.email} onChange={(e) => setNv({ ...nv, email: e.target.value })} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lblStyle}>Website (optional)</label>
            <input className="input-field" value={nv.url} onChange={(e) => setNv({ ...nv, url: e.target.value })} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lblStyle}>Note (optional)</label>
            <input className="input-field" value={nv.note} onChange={(e) => setNv({ ...nv, note: e.target.value })} />
          </div>
        </div>
        <button data-testid="bundle-vendor-add" type="button" className="btn-secondary" onClick={addVendor} style={{ alignSelf: "flex-start" }}>
          <Plus size={14} /> Add vendor
        </button>
        {vendors.length > 0 && (
          <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
            {vendors.map((v, i) => (
              <div key={v.id || i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderTop: i ? "1px solid var(--border)" : "none", gap: 10 }} data-testid={`bundle-vendor-row-${i}`}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>{v.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {[v.category, v.phone, v.email].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <button className="btn-ghost" onClick={() => removeVendor(i)} data-testid={`bundle-vendor-remove-${i}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}

        {err && <div style={{ color: "var(--secondary)", fontSize: 13 }} data-testid="bundle-editor-err">{err}</div>}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
        <button onClick={onClose} className="btn-ghost" disabled={busy}>Cancel</button>
        <button data-testid="bundle-save-btn" onClick={save} className="btn-primary" disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Save size={14} /> {busy ? "Saving…" : (isNew ? "Save bundle" : "Update bundle")}
        </button>
      </div>
    </Modal>
  );
}

/* ---------- Apply Modal ---------- */
function ApplyBundleModal({ bundle, sellers, onClose, onApplied }) {
  const [picked, setPicked] = useState(new Set());
  const [opts, setOpts] = useState({
    apply_custom_tasks: true,
    apply_agent_note: false,
    apply_key_dates: false,
    apply_custom_vendors: true,
    replace_tasks: false,
    replace_vendors: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  // Which of the picked clients already had this template put on them.
  const [dupes, setDupes] = useState([]);
  const [done, setDone] = useState(0);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sellers;
    return sellers.filter((s) =>
      (s.name || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q) ||
      (s.property_address || "").toLowerCase().includes(q)
    );
  }, [sellers, search]);

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (filtered.every((s) => picked.has(s._id))) {
      setPicked((prev) => {
        const next = new Set(prev);
        filtered.forEach((s) => next.delete(s._id));
        return next;
      });
    } else {
      setPicked((prev) => {
        const next = new Set(prev);
        filtered.forEach((s) => next.add(s._id));
        return next;
      });
    }
  };

  const applyTo = async (ids, force) => {
    if (!ids.length) { setErr("Pick at least one client."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/bundles/${bundle.id}/apply`, {
        seller_ids: ids,
        ...opts,
        force,
      });
      const again = (data.skipped || []).filter((s) => s.reason === "Already applied");
      setDupes(again);
      setDone((data.applied || []).length);
      // Only close once nothing is waiting on a decision, otherwise the
      // agent never finds out that some clients were left alone.
      if (!again.length) onApplied(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Couldn't apply this template.");
    } finally { setBusy(false); }
  };

  const apply = () => applyTo(Array.from(picked), false);
  // Re-applying goes only to the ones that were held back, so the clients
  // who just received it do not get a second copy of everything.
  const applyAgain = () => applyTo(dupes.map((d) => d.seller_id), true);

  return (
    <Modal onClose={onClose} title={`Apply "${bundle.name}"`} testid="bundle-apply-modal">
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="card" style={{ padding: 14, background: "var(--surface-alt)" }}>
          <div className="overline">What to apply</div>
          <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", marginTop: 10 }}>
            <Check label={`Custom tasks (${(bundle.custom_tasks || []).length})`} testid="apply-tasks" checked={opts.apply_custom_tasks} onChange={(v) => setOpts({ ...opts, apply_custom_tasks: v })} />
            <Check label="Agent note" testid="apply-note" checked={opts.apply_agent_note} onChange={(v) => setOpts({ ...opts, apply_agent_note: v })} disabled={!bundle.agent_note} />
            <Check label="Key dates" testid="apply-dates" checked={opts.apply_key_dates} onChange={(v) => setOpts({ ...opts, apply_key_dates: v })} disabled={!Object.values(bundle.key_dates || {}).some(Boolean)} />
            <Check label={`Vendors (${(bundle.custom_vendors || []).length})`} testid="apply-vendors" checked={opts.apply_custom_vendors} onChange={(v) => setOpts({ ...opts, apply_custom_vendors: v })} />
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 12, fontSize: 13, color: "var(--text-muted)" }}>
            <Check label="Replace existing tasks" testid="replace-tasks" checked={opts.replace_tasks} onChange={(v) => setOpts({ ...opts, replace_tasks: v })} disabled={!opts.apply_custom_tasks} />
            <Check label="Replace existing vendors" testid="replace-vendors" checked={opts.replace_vendors} onChange={(v) => setOpts({ ...opts, replace_vendors: v })} disabled={!opts.apply_custom_vendors} />
          </div>
        </div>

        <div>
          <label style={lblStyle}>Clients to apply this template to</label>
          <input data-testid="apply-search" className="input-field" placeholder="Search clients…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{picked.size} selected</span>
            <button type="button" onClick={toggleAll} className="btn-ghost" style={{ fontSize: 13 }} data-testid="apply-toggle-all">
              {filtered.length > 0 && filtered.every((s) => picked.has(s._id)) ? "Deselect filtered" : "Select filtered"}
            </button>
          </div>
          <div style={{ marginTop: 8, maxHeight: 260, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 12 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 16, color: "var(--text-muted)" }}>No clients match.</div>
            ) : filtered.map((s, i) => (
              <label key={s._id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderTop: i ? "1px solid var(--border)" : "none", cursor: "pointer" }} data-testid={`apply-seller-${s._id}`}>
                <input type="checkbox" checked={picked.has(s._id)} onChange={() => toggle(s._id)} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>
                    {s.name || s.email}
                    {s.co_owners && s.co_owners.length > 0 && <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> + {s.co_owners.length} co-owner</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{s.email}{s.property_address ? ` · ${s.property_address}` : ""}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {dupes.length > 0 && (
          <div data-testid="already-applied" className="card"
               style={{ padding: "12px 14px", fontSize: 13, background: "var(--surface-alt)" }}>
            <b style={{ fontWeight: 600 }}>
              {dupes.length === 1
                ? "One client already has this template"
                : `${dupes.length} clients already have this template`}
            </b>
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {dupes.map((d) => (
                <li key={d.seller_id}>
                  {d.name || d.email}
                  {d.applied_at ? `, added ${new Date(d.applied_at).toLocaleDateString()}` : ""}
                </li>
              ))}
            </ul>
            <p style={{ margin: "8px 0 0", color: "var(--text-muted)" }}>
              {done > 0
                ? `Applied to the other ${done}. Adding it again here would give them a second copy of every task and vendor.`
                : "Adding it again would give them a second copy of every task and vendor."}
            </p>
            <button type="button" data-testid="apply-again" className="btn-secondary"
                    style={{ marginTop: 10 }} disabled={busy} onClick={applyAgain}>
              Add a second copy anyway
            </button>
          </div>
        )}
        {err && <div style={{ color: "var(--secondary)", fontSize: 13 }} data-testid="apply-err">{err}</div>}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
        <button onClick={onClose} className="btn-ghost" disabled={busy}>Cancel</button>
        <button data-testid="apply-confirm" onClick={apply} className="btn-primary" disabled={busy || picked.size === 0} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Users size={14} /> {busy ? "Applying…" : `Apply to ${picked.size || ""} client${picked.size === 1 ? "" : "s"}`.trim()}
        </button>
      </div>
    </Modal>
  );
}

/* ---------- Snapshot from seller ---------- */
function SnapshotFromSellerModal({ sellers, onClose, onCreated }) {
  const [sellerId, setSellerId] = useState(sellers[0]?._id || "");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (!sellerId) { setErr("Pick a client."); return; }
    if (!name.trim()) { setErr("Give this template a name."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/admin/bundles/from-seller/${sellerId}`, { name: name.trim(), description: description.trim() });
      onCreated(data.bundle);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Couldn't save the template.");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Save a template from an existing client" testid="bundle-snapshot-modal">
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0 }}>
          Saves this client's current tasks, note, key dates and vendors as a template you can put on someone else later.
        </p>
        <div>
          <label style={lblStyle}>Client</label>
          <select data-testid="snapshot-seller" className="input-field" value={sellerId} onChange={(e) => setSellerId(e.target.value)}>
            {sellers.map((s) => <option key={s._id} value={s._id}>{s.name || s.email} · {s.email}</option>)}
          </select>
        </div>
        <div>
          <label style={lblStyle}>Template name</label>
          <input data-testid="snapshot-name" className="input-field" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. The Smith launch playbook" />
        </div>
        <div>
          <label style={lblStyle}>Description (optional)</label>
          <input data-testid="snapshot-desc" className="input-field" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        {err && <div style={{ color: "var(--secondary)", fontSize: 13 }} data-testid="snapshot-err">{err}</div>}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
        <button onClick={onClose} className="btn-ghost" disabled={busy}>Cancel</button>
        <button data-testid="snapshot-save" onClick={save} className="btn-primary" disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Save size={14} /> {busy ? "Saving…" : "Save bundle"}
        </button>
      </div>
    </Modal>
  );
}

/* ---------- Shared little bits ---------- */
const lblStyle = { fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 };

function Divider({ label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
      <div className="overline" style={{ color: "var(--primary)" }}>{label}</div>
      <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
    </div>
  );
}

function Check({ label, checked, onChange, testid, disabled }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} data-testid={testid} />
      <span>{label}</span>
    </label>
  );
}

function Modal({ onClose, title, children, testid }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,25,23,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 50 }} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: "100%", maxWidth: 720, maxHeight: "90vh", overflowY: "auto", padding: 28, background: "var(--bg)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>{title}</h2>
          <button onClick={onClose} className="btn-ghost" data-testid={`${testid}-close`}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
